import React, { useEffect } from 'react';
import { actions, containers, elements, feedback } from '@ecosystem/newron-design-system';

import { ClientSidebar } from '../../content';
import { ConfigItem, ConfigItemValue } from '../types';

import { ConfigItemElement } from './ConfigItemElement';
import { ConfigItemsTrayContextProvider } from './ConfigItemsTrayContextProvider';
import { TrayContentWrapper } from './TrayContentWrapper';

const { Tray } = containers;
const { Button, IconButton, ButtonGroup } = actions;
const { Icon } = elements;

export interface ConfigItemsTrayProps {
  configItems: ConfigItem[];
  configTrayTitle: string;
  configTrayError?: string;
  configItemsLoading?: boolean;
  onValueChange: (configValue: ConfigItemValue) => void;
  onRequestOptions?: (key: string) => void;
  onApply: () => void;
  onClear: () => void;
  onClose?: () => void;
}

export const ConfigItemsTray = ({
  configItems,
  configTrayTitle,
  configTrayError,
  configItemsLoading = false,
  onApply,
  onValueChange,
  onRequestOptions,
  onClear,
  onClose,
}: ConfigItemsTrayProps) => {
  const { hideSidebar, openSidebar } = ClientSidebar.useSidebar({
    key: 'config-items-tray',
    expanded: true,
  });

  const handleClose = () => {
    hideSidebar();
    onClose?.();
  };

  // when rendering, we open the sidebar, on unmount we hide it
  // we are not calling onClose() because that would persist the closed state in session storage
  useEffect(() => {
    openSidebar();
    return () => hideSidebar();
  }, [openSidebar, hideSidebar]);

  return (
    <ClientSidebar.Content>
      <ConfigItemsTrayContextProvider>
        <Tray>
          <Tray.Header>
            <div className="flex justify-between w-full h-14 items-center ">
              <p className="text-base">{configTrayTitle}</p>
              <IconButton
                color="secondaryInteractive"
                onClick={handleClose}
                variant="bgLight"
                mode="tertiary"
                testId="close-config-tray"
              >
                <Icon.icons.Close />
              </IconButton>
            </div>
          </Tray.Header>
          <TrayContentWrapper isLoading={configItemsLoading}>
            {configItems?.map((configItem, index) => (
              <ConfigItemElement
                key={configItem.key}
                index={index}
                configItem={configItem}
                onChange={(key, value) => onValueChange({ key, value })}
                depth={0}
                onRequestOptions={onRequestOptions}
              />
            ))}
          </TrayContentWrapper>
          <Tray.Footer errorMessage={configTrayError}>
            <div className="flex-grow">
              <ButtonGroup>
                <ButtonGroup.PrimaryButton>
                  <Button
                    onClick={onApply}
                    stretch
                    testId="apply-config-values"
                    disabled={!!configTrayError || configItemsLoading}
                  >
                    Apply
                  </Button>
                </ButtonGroup.PrimaryButton>
                <ButtonGroup.SecondaryButtons>
                  <Button onClick={onClear} mode="secondary" stretch testId="clear-config-values">
                    Clear
                  </Button>
                </ButtonGroup.SecondaryButtons>
              </ButtonGroup>
            </div>
          </Tray.Footer>
        </Tray>
      </ConfigItemsTrayContextProvider>
    </ClientSidebar.Content>
  );
};
